import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Network, Cloud, HardDrive, Cable, MonitorCog, BadgeCheck, ArrowRight, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useContent, parseList, parseLines } from "@/lib/content";

const SERVICE_ICONS = [ShieldCheck, Network, MonitorCog, Cloud, HardDrive, Cable];
const SLA_COLORS = ["text-biz-orange", "text-biz-cyan", "text-biz-green", "text-slate-400"];

export default function BusinessIT() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", employees: "1-10", message: "" });
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const content = useContent();
  const svcList = parseList(content["it.services"]).filter((r) => r[0]).map((r, i) => ({ icon: SERVICE_ICONS[i] || ShieldCheck, title: r[0] || "", desc: r[1] || "", tag: r[2] || "" }));
  const sla = parseList(content["it.sla"]).filter((r) => r[0]).map((r, i) => ({ level: r[0] || "", example: r[1] || "", response: r[2] || "", color: SLA_COLORS[i] || "text-slate-400" }));
  const badges = parseLines(content["it.badges"]);
  const ih1 = (content["it.h1"] || "").split("|");

  const toggleService = (s) => setServices(services.includes(s) ? services.filter((x) => x !== s) : [...services, s]);

  const submit = async () => {
    if (!form.name || !form.email || !form.company) return toast.error("Name, email, and company are required.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions/it-consultation", { ...form, services });
      setDone(data);
      toast.success("Consultation requested. An engineer will reach out within 1 business day.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-biz-bg text-slate-100" data-testid="business-it-page">
      <section className="relative overflow-hidden border-b border-slate-700/60">
        <img src={content["images.it_hero"]} alt="Network infrastructure" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-biz-bg via-biz-bg/85 to-biz-bg/40" />
        <div className="absolute inset-0 grid-bg-biz" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-biz-cyan">{content["it.eyebrow"]}</p>
          <h1 className="max-w-3xl font-biz text-4xl font-extrabold uppercase tracking-wide text-white sm:text-6xl" data-testid="business-it-heading">
            {ih1[0]} <span className="text-biz-blue">{ih1[1]}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {content["it.intro"]}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {badges.map((b) => (
              <span key={b} className="flex items-center gap-1.5 rounded-sm border border-slate-600 bg-biz-card px-3 py-1.5 font-mono text-[11px] tracking-widest text-biz-cyan">
                <Lock className="h-3 w-3" /> {b}
              </span>
            ))}
          </div>
          <a href="#it-consult" data-testid="business-it-hero-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-sm bg-biz-blue px-7 py-3.5 font-biz text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-biz-cyan hover:text-biz-bg">
            Schedule IT Consultation <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="it-services-section">
        <h2 className="mb-10 font-biz text-2xl font-bold uppercase tracking-wide text-white sm:text-4xl">{content["it.services_h"]}</h2>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {svcList.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="biz-card-hover rounded-sm border border-slate-700/60 bg-biz-card p-6" data-testid={`it-service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="mb-4 flex items-center justify-between">
                <s.icon className="h-7 w-7 text-biz-cyan" />
                <span className="font-mono text-[10px] tracking-[0.3em] text-slate-500">{s.tag}</span>
              </div>
              <h3 className="font-biz text-xl font-bold uppercase tracking-wide text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SLA MATRIX */}
      <section className="border-y border-slate-700/60 bg-biz-card/40" data-testid="sla-section">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <div>
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-biz-cyan">{content["it.sla_eyebrow"]}</p>
            <h2 className="font-biz text-2xl font-bold uppercase tracking-wide text-white sm:text-4xl">{content["it.sla_h"]}</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
              {content["it.sla_text"]}
            </p>
            <div className="mt-6 overflow-hidden rounded-sm border border-slate-700/60">
              {sla.map((s, i) => (
                <div key={s.level} data-testid={`sla-row-${i}`}
                  className={`flex items-center justify-between gap-3 px-5 py-4 ${i % 2 ? "bg-biz-card/70" : "bg-biz-card/30"}`}>
                  <div>
                    <p className={`font-mono text-xs font-bold uppercase tracking-wider ${s.color}`}>{s.level}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{s.example}</p>
                  </div>
                  <span className="font-biz text-lg font-bold uppercase text-white">{s.response}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative overflow-hidden rounded-sm border border-slate-700/60">
            <img src={content["images.it_rack"]} alt="Server rack" loading="lazy" className="h-full min-h-[340px] w-full object-cover opacity-80" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-sm bg-biz-bg/90 px-4 py-2 backdrop-blur">
              <BadgeCheck className="h-4 w-4 text-biz-green" />
              <span className="font-mono text-xs text-slate-200">{content["it.rack_badge"]}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CONSULT FORM */}
      <section id="it-consult" className="mx-auto max-w-4xl scroll-mt-24 px-4 py-20 sm:px-6" data-testid="it-consult-section">
        <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-biz-cyan">{content["it.consult_eyebrow"]}</p>
        <h2 className="mb-10 font-biz text-2xl font-bold uppercase tracking-wide text-white sm:text-4xl">{content["it.consult_h"]}</h2>

        {done ? (
          <div className="rounded-sm border border-biz-green/40 bg-biz-card p-10 text-center" data-testid="it-consult-success">
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-biz-green" />
            <h3 className="font-biz text-2xl font-bold uppercase tracking-wide text-white">Request Received</h3>
            <p className="mt-2 font-mono text-sm text-biz-cyan" data-testid="it-consult-reference">Ref: {done.id}</p>
            <p className="mt-4 text-sm text-slate-400">A CISSP-level engineer will contact you within 1 business day.</p>
          </div>
        ) : (
          <div className="rounded-sm border border-slate-700/60 bg-biz-card p-6 sm:p-10">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-xs text-slate-400">Your name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="it-name-input" className="border-slate-700 bg-biz-bg text-white" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Work email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="it-email-input" className="border-slate-700 bg-biz-bg text-white" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Company *</Label>
                <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="it-company-input" className="border-slate-700 bg-biz-bg text-white" />
              </div>
              <div>
                <Label className="text-xs text-slate-400">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="it-phone-input" className="border-slate-700 bg-biz-bg text-white" />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs text-slate-400">Team size</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {["1-10", "11-25", "26-50", "50+"].map((s) => (
                  <button key={s} data-testid={`it-team-size-${s.replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setForm({ ...form, employees: s })}
                    className={`rounded-sm border px-4 py-2 font-mono text-xs transition-colors ${form.employees === s ? "border-biz-blue bg-biz-blue/15 text-biz-cyan" : "border-slate-700 text-slate-400 hover:border-biz-blue/60"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs text-slate-400">What do you need?</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {svcList.map((s) => (
                  <button key={s.title} data-testid={`it-need-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => toggleService(s.title)}
                    className={`rounded-sm border px-3 py-2 font-mono text-xs transition-colors ${services.includes(s.title) ? "border-biz-blue bg-biz-blue/15 text-biz-cyan" : "border-slate-700 text-slate-400 hover:border-biz-blue/60"}`}>
                    {s.title}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-xs text-slate-400">Current setup & pain points</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4}
                data-testid="it-message-input" placeholder="e.g. 14 Windows laptops, one aging server, no backups, worried about ransomware..."
                className="border-slate-700 bg-biz-bg text-white" />
            </div>
            <button onClick={submit} disabled={submitting} data-testid="it-consult-submit-button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-biz-blue px-6 py-3.5 font-biz text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-biz-cyan hover:text-biz-bg disabled:opacity-60">
              {submitting ? "Sending..." : "Request Discovery Call"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
